import { ITEM_KIND } from "../../StrategyCanvas/StrategyCanvasSummary";
import { COMMENT_SCOPE, REVIEW_STATUS } from "../_config/quarterlyOkrConfig";
import { commentsFor } from "./useReviewActions";

// The "Review Summary" groups (Strategy Canvas's `SummaryGroups` shape, as
// the Annual OKR review builds them): only what there is something to say
// about — a to-discuss mark or a comment. `color` is a `sp-section-rail--*`
// suffix, not a hex value.

const hasText = (comment) => Boolean(comment?.comment_text?.trim());

const objectiveItems = (objectives, type, comments, titleOf) =>
  (objectives || [])
    .map((objective) => {
      const own = commentsFor(comments, {
        scope: COMMENT_SCOPE.OBJECTIVE,
        type,
        objectiveId: objective.objective_id,
      }).filter(hasText);
      const flagged = objective.review?.status === REVIEW_STATUS.TO_DISCUSS;
      if (!flagged && !own.length) return null;
      return {
        id: `${type}-${objective.objective_id}`,
        kind: ITEM_KIND.BLOCK,
        title: titleOf(objective),
        status: objective.review?.status || null,
        comments: own,
      };
    })
    .filter(Boolean);

export const buildReviewSummary = (review) => {
  if (!review) return [];
  const comments = review.comments || [];
  const groups = [
    {
      key: "company",
      name: "Company OKRs",
      color: "core",
      items: objectiveItems(
        review.company_objectives,
        "company",
        comments,
        (o) => o.objective,
      ),
    },
    // One group per person, named once: comments about them first, then
    // their objectives — the name is never repeated before each objective.
    ...(review.members || []).map((member) => {
      const aboutThem = commentsFor(comments, {
        scope: COMMENT_SCOPE.MEMBER,
        userId: member.user_id,
      }).filter(hasText);
      return {
        key: `individual-${member.user_id}`,
        name: member.name,
        color: "dna",
        items: [
          ...(aboutThem.length
            ? [
                {
                  id: `member-${member.user_id}`,
                  kind: ITEM_KIND.SECTION,
                  // Not the Canvas "Section comment": it's about a person.
                  badge: "Person comment",
                  title: `About ${member.name}`,
                  status: null,
                  comments: aboutThem,
                },
              ]
            : []),
          ...objectiveItems(
            (review.individual_objectives || []).filter(
              (o) => String(o.member?.user_id) === String(member.user_id),
            ),
            "individual",
            comments,
            (o) => o.objective,
          ),
        ],
      };
    }),
    {
      key: "overall",
      name: "The quarter as a whole",
      color: "dark",
      items: commentsFor(comments, { scope: COMMENT_SCOPE.COMPANY })
        .filter(hasText)
        .map((comment) => ({
          id: `company-${comment.coach_comment_id}`,
          kind: ITEM_KIND.OVERALL,
          title: "Overall",
          status: null,
          comments: [comment],
        })),
    },
  ];
  return groups.filter((group) => group.items.length);
};
